/**
 * AcademiasTable Component
 * 
 * REESTRUCTURADA para seguir exactamente el patrón de ProgramsTable:
 * - Usa estructura de divs con grid layout
 * - Mismos estilos de ProgramasAcademicos
 * - Recibe datos desde props (no carga internamente)
 * - Manejo de loading y estado vacío
 * - Botones de acción con iconos
 * - Columnas: Nombre, Código, Descripción, Estado, Acciones
 * - Paginación incluida
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import styles from "../../styles/PantallaDeProgramasAcademicos.v2.module.css";
import Pagination from "../../components/Pagination.tsx";

function AcademiasTable({ academias = [], loading = false, query = '', onView, onEdit, onDelete }) {
  // Paginación
  const [page, setPage] = useState(1);
  const pageSize = 8;
  
  // Crear clave única basada en IDs para detectar cambios reales
  const academiasKey = useMemo(() => {
    return academias.map(a => a.id_academia || a.id).sort().join(',');
  }, [academias]);

  const prevAcademiasKeyRef = useRef(academiasKey);

  // Reiniciar página solo cuando realmente cambia la lista
  useEffect(() => {
    if (prevAcademiasKeyRef.current !== academiasKey) {
      setPage(1);
      prevAcademiasKeyRef.current = academiasKey;
    }
  }, [academiasKey]);

  const totalPages = Math.max(1, Math.ceil(academias.length / pageSize));

  // Ajustar página si quedó fuera de rango
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginated = academias.slice((page - 1) * pageSize, page * pageSize);
  const isSearching = (query || '').toString().trim().length > 0;

  // Botones de acción
  const ActionButtons = ({ academia }) => (
    <div className={styles.actionButtons}>
      <button 
        aria-label="Ver detalles" 
        className={styles.actionButton} 
        onClick={() => onView?.(academia)}
      >
        <img
          src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/a1782ac5b190a9d29d8f798383eedda3f294d56b?placeholderIfAbsent=true"
          alt="Ver"
          className={styles.actionIcon}
        />
      </button>
      <button 
        aria-label="Editar" 
        className={styles.actionButton} 
        onClick={() => onEdit?.(academia)}
      >
        <img
          src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/83eacec9f1f974b33615bbc95aea7ff7b2d13b0e?placeholderIfAbsent=true"
          alt="Editar"
          className={styles.actionIcon}
        />
      </button>
      <button 
        aria-label="Eliminar" 
        className={styles.actionButton} 
        onClick={() => onDelete?.(academia)}
      >
        <img
          src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/bef20ed4bd5ed293db48e37df8e511dca0fed7db2e?placeholderIfAbsent=true"
          alt="Eliminar"
          className={styles.actionIcon}
        />
      </button>
    </div>
  );

  // Header de la tabla
  const Header = () => (
    <div className={styles.tableHeader}>
      <div className={styles.tableTitle}>
        <div className={styles.tableTitleIcon}>
          <div className={styles.iconContainerOrange}>
            <span>🎓</span>
          </div>
        </div>
        <h2 className={styles.tableTitleText}>Academias</h2>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.tableScrollWrapper}>
          <Header />
          <div style={{ padding: '20px', textAlign: 'center' }}>Cargando academias...</div>
        </div>
      </div>
    );
  }

  if (!paginated.length) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.tableScrollWrapper}>
          <Header />
          <div style={{ padding: '20px', textAlign: 'center' }}>
            {isSearching
              ? `Sin resultados para "${query}".`
              : 'No hay academias registradas.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer} style={{
      '--col-prog': 'minmax(200px, 1.2fr)',
      '--col-campus': 'minmax(100px, 0.8fr)',
      '--col-seccion': 'minmax(200px, 1.5fr)',
      '--col-codigo': 'minmax(100px, 0.8fr)',
      '--col-periodo': '0',
      '--col-creditos': '0',
      '--col-estado': 'minmax(100px, 0.8fr)',
      '--col-acciones': 'minmax(140px, 0.7fr)'
    }}>
      <div className={styles.tableScrollWrapper}>
        <Header />

        {/* Header de columnas */}
        <div className={styles.tableHeaderRow} style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(200px, 1.2fr) minmax(100px, 0.8fr) minmax(200px, 1.5fr) minmax(100px, 0.8fr) minmax(140px, 0.7fr)'
        }}>
          <div className={styles.columnHeader}>
            <span>Nombre de Academia</span>
          </div>
          <span className={styles.columnHeader}>Código</span>
          <span className={styles.columnHeader}>Descripción</span>
          <div className={styles.columnHeader}>
            <span>Estado</span>
          </div>
          <span className={styles.columnHeader}>Acciones</span>
        </div>

        {/* Cuerpo de la tabla */}
        <div className={styles.tableBody}>
          {paginated.map((academia, index) => (
            <div 
              key={academia.id || index} 
              className={styles.tableRow}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(200px, 1.2fr) minmax(100px, 0.8fr) minmax(200px, 1.5fr) minmax(100px, 0.8fr) minmax(140px, 0.7fr)'
              }}
            >
              <div className={styles.programInfo}>
                <span className={styles.programName}>{academia.nombre || '—'}</span>
              </div>
              <span className={styles.cellData}>{academia.codigo || '—'}</span>
              <span className={styles.cellData}>{academia.descripcion || '—'}</span>
              <div className={styles.statusCell}>
                <div className={academia.estado === 'ACTIVO' ? styles.statusActive : styles.statusInactive}>
                  <span>{academia.estado || 'ACTIVO'}</span>
                </div>
              </div>
              <ActionButtons academia={academia} />
            </div>
          ))}
        </div>

        {/* Paginación */}
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}

export default React.memo(AcademiasTable);
